/**
 * Pure phase request mappers (#2239). Kept free of React/API-client imports
 * so the contract is browserless-testable.
 *
 * Every field is assigned individually so the compiler guards schema drift —
 * no blanket casts. Enum values (phase type, metric type) are guarded at
 * runtime: unknown values throw loudly instead of being smuggled to the API.
 *
 * Phase DELETE exists in the contract and is intentionally NOT exposed as a
 * hook: patient flows edit phases via PATCH (steps are cleared by clearing
 * their data) and cycles are deleted wholesale. Deleting a single phase
 * would silently drop its verified sub-records (trigger shot, metrics,
 * spermiograms). Revisit only with an explicit product decision.
 */

import type { paths } from "@src/packages/openapi/schema";
import type { PhaseEmbryoTransferInfoBody } from "@src/packages/patients/embryoTransferInfo";

type CreatePhaseBody = NonNullable<
  paths["/{tenantId}/cycles/{id}/phases"]["post"]["requestBody"]
>["content"]["application/json"];
type UpdatePhaseBody = NonNullable<
  paths["/{tenantId}/cycles/{id}/phases/{phaseId}"]["patch"]["requestBody"]
>["content"]["application/json"];

export type PhaseSpermiogramInput = NonNullable<
  CreatePhaseBody["spermiograms"]
>[number];
export type PhaseProcedureInput = Exclude<
  NonNullable<CreatePhaseBody["procedures"]>[number],
  { op: "delete" }
>;
export type PhaseInseminationInfoInput = NonNullable<
  CreatePhaseBody["inseminationInfo"]
>;
export type PhaseTestResult = NonNullable<
  UpdatePhaseBody["testResults"]
>[number];


export type PhaseSubObjects = {
  triggerShot?: {
    id?: string;
    date?: string | null;
    time?: string | null;
    notes?: string | null;
  } | null;
  spermiograms?: {
    id?: string;
    patientId: string;
    semenSpecimenId?: string | null;
    origin?: "partner" | "donor" | null;
    spermType?: "fresh" | "frozen" | null;
    technique?: PhaseSpermiogramInput["technique"];
    preparationMethod?: PhaseSpermiogramInput["preparationMethod"];
    date?: string | null;
    time?: string | null;
    notes?: string | null;
  }[];
  procedures?: PhaseProcedureInput[];
  inseminationInfo?: {
    id?: string;
    date?: string | null;
    time?: string | null;
    inseminationType?: PhaseInseminationInfoInput["inseminationType"];
    catheterType?: PhaseInseminationInfoInput["catheterType"];
    catheterBrand?: string | null;
    ultrasoundGuided?: boolean | null;
    technique?: PhaseInseminationInfoInput["technique"];
    inseminationDuration?: number | null;
    notes?: string | null;
  };
  eggRetrievalInfo?: { id?: string; notes?: string | null };
  embryoTransferInfo?: PhaseEmbryoTransferInfoBody;
  embryoData?: { id?: string; code?: string | null; grade?: string | null }[];
  testResults?: PhaseTestResult[];
};

const PHASE_TYPES = [
  "protocol",
  "egg_retrieval",
  "fertilization",
  "sperm_collection",
  "insemination",
  "embryo_development",
  "transfer",
  "luteal_phase",
  "pre_treatment",
] as const;
type PhaseType = (typeof PHASE_TYPES)[number];

function assertPhaseType(value: string): PhaseType {
  if ((PHASE_TYPES as readonly string[]).includes(value)) {
    return value as PhaseType;
  }
  throw new Error(`Unknown phase type "${value}" — not in the API contract`);
}

/**
 * Metric types the phase contract accepts — mirrors the generated enum so
 * unknown metric keys fail loudly at the mapper instead of being smuggled
 * past the compiler and silently rejected server-side.
 */
const PHASE_METRIC_TYPES = [
  "follicles_over_17_mm",
  "m2_count",
  "motility_deprecated",
  "transferred_embryo",
  "frozen_embryo",
  "retrieved_oocytes",
  "fertilized_oocytes",
  "estrogen_min",
  "estrogen_max",
  "tmsc_deprecated",
  "endometrial_thickness",
  "embryos_day_3",
  "embryos_day_5",
] as const;
type PhaseMetricType = (typeof PHASE_METRIC_TYPES)[number];

function isPhaseMetricType(value: string): value is PhaseMetricType {
  return (PHASE_METRIC_TYPES as readonly string[]).includes(value);
}

function mapPhaseMetrics(
  metrics: { id?: string; type: string; value: number | null }[] | undefined,
): { id?: string; type: PhaseMetricType; value: number | null }[] | undefined {
  if (!metrics) return undefined;
  return metrics.map((metric) => {
    if (!isPhaseMetricType(metric.type)) {
      throw new Error(
        `Unknown phase metric type "${metric.type}" — not in the API contract`,
      );
    }
    return { ...metric, type: metric.type };
  });
}

export type CreatePhaseInput = {
  type: string;
  startDate: string;
  startTime?: string | null;
  endDate?: string | null;
  fertilityMetrics?: { type: string; value: number | null }[];
} & PhaseSubObjects;

export type UpdatePhaseInput = {
  startDate?: string | null;
  startTime?: string | null;
  endDate?: string | null;
  fertilityMetrics?: { id?: string; type: string; value: number | null }[];
} & PhaseSubObjects;

function mapSubObjects(body: PhaseSubObjects) {
  return {
    triggerShot: body.triggerShot,
    spermiograms: body.spermiograms,
    procedures: body.procedures,
    inseminationInfo: body.inseminationInfo,
    eggRetrievalInfo: body.eggRetrievalInfo,
    embryoTransferInfo: body.embryoTransferInfo,
    embryoData: body.embryoData,
    testResults: body.testResults,
  };
}

export function toCreatePhaseBody(body: CreatePhaseInput): CreatePhaseBody {
  return {
    type: assertPhaseType(body.type),
    startDate: body.startDate,
    startTime: body.startTime,
    endDate: body.endDate,
    fertilityMetrics: mapPhaseMetrics(body.fertilityMetrics),
    ...mapSubObjects(body),
  };
}

export function toUpdatePhaseBody(body: UpdatePhaseInput): UpdatePhaseBody {
  return {
    startDate: body.startDate,
    startTime: body.startTime,
    endDate: body.endDate,
    fertilityMetrics: mapPhaseMetrics(body.fertilityMetrics),
    ...mapSubObjects(body),
  };
}
